import React, { useEffect, useState } from 'react';
import AppDetailsContainer from '~/components/page/Apps/components/AppDetailsContainer';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import { isEmpty } from 'ramda';
import {
  AppValuationReport_Update,
  useGetAppValuationReportRouteAvailablityLazyQuery,
} from '~/graphql/types';
import useDebounce from '~/hooks/useDebounce';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import Input from '~/components/page/Apps/components/Input';
import { useRecoilState } from 'recoil';
import { defaultErrorsByPath } from '../../state';
import { WAARDERAPPORT_URL_PREFIX } from '~/components/page/Apps/ValueReport';
import { URL_STATUS_INDICATORS } from '~/styles/indicators';

const text = {
  routeUnavailable: 'Deze route is niet beschikbaar',
  emptyMessage: 'Het veld kan niet leeg zijn',
  validationMessage:
    'Alleen letters, cijfers en het streepje(-) zijn toegestaan',
  checkAvailability: 'Beschikbaarheid controleren',
  available: 'Beschikbaar',
  notAvailable: 'Niet beschikbaar',
};

const ROUTE_VALIDATION_REGEX = /^([a-z0-9]|-)+$/;
const pathname = '/';

export type Props = RouteComponentProps & {
  dataTestId?: string;
  loading: boolean;
  data?: AppValuationReport_Update['route'] | null;
  updated?: AppValuationReport_Update['route'] | null;
  reportId: string;
  onUpdate: (value: AppValuationReport_Update['route']) => void;
};

const General: React.FCC<Props> = ({
  data,
  updated,
  loading,
  reportId,
  onUpdate,
}) => {
  const { id: accountId } = useCurrentAccount();
  const [errorsByPath, setErrorsByPath] = useRecoilState(defaultErrorsByPath);

  const [statusIdx, setStatusIdx] = useState<number | undefined>(undefined);

  const onRouteUnavailable = () => {
    setStatusIdx(2);
    setErrorsByPath(prev => ({
      ...prev,
      [pathname]: text.routeUnavailable,
    }));
  };

  const [getAvailability, { loading: queryLoading }] =
    useGetAppValuationReportRouteAvailablityLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: data => {
        if (data.isAvailable) {
          setStatusIdx(1);
          setErrorsByPath(prev => ({ ...prev, [pathname]: null }));
        } else {
          onRouteUnavailable();
        }
      },
      onError: () => onRouteUnavailable(),
    });

  const debouncedRoute = useDebounce(updated, 300);

  useEffect(() => {
    if (
      data !== updated &&
      debouncedRoute &&
      error !== text.validationMessage
    ) {
      void getAvailability({
        variables: {
          accountId,
          id: reportId,
          route: debouncedRoute,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRoute]);

  const error = errorsByPath[pathname];

  return (
    <AppDetailsContainer
      header="Algemeen"
      icon="gear"
      loading={loading}
      dataTestId="design-page"
    >
      <Input
        name="url-link"
        statuses={!isEmpty(updated) ? URL_STATUS_INDICATORS : undefined}
        statusIndex={queryLoading ? 0 : statusIdx}
        prefix={`https://${WAARDERAPPORT_URL_PREFIX}`}
        label="Gewenste adres"
        reset="Bekijk live"
        onReset={() => {
          window.open(
            `https://${WAARDERAPPORT_URL_PREFIX}${data}`,
            '_blank',
            'noopener,noreferrer',
          );
        }}
        error={error || undefined}
        value={updated}
        onChange={async value => {
          onUpdate(value);

          if (!value) {
            setErrorsByPath(prev => ({
              ...prev,
              [pathname]: text.emptyMessage,
            }));
          } else if (!ROUTE_VALIDATION_REGEX.test(value)) {
            setErrorsByPath(prev => ({
              ...prev,
              [pathname]: text.validationMessage,
            }));
          } else {
            setErrorsByPath(prev => ({ ...prev, [pathname]: null }));
          }
        }}
      />
    </AppDetailsContainer>
  );
};

export default General;
